import { ReactElement, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AllActivityTypesFilterChip } from './AllActivityTypesFilterChip';
import { SearchFilterInput } from 'components/filters/SearchFilterInput';
import {
  ModelScene,
  IndexScene,
  CreateScene,
  EntityScene,
  UpdateScene,
  useEntity,
  EntityAction,
  EntityFormActions,
} from 'components';
import permissions from 'modules/supplier/outletOrServiceCategory/serviceCategoryPermissions';
import { Alert, Box } from '@ff-it/ui';
import { ControlledTableColumns } from 'components/ControlledTable';
import type { ServiceCategory } from 'modules/supplier/outletOrServiceCategory/types';
import { OutletOrServiceCategoryForm } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryForm';
import { Nav } from './Nav';
import { ServiceCategoryProducts } from './ServiceCategoryProducts';
import { ImportExportActions } from 'components/ListActions';
import { openCatalogAction } from 'components/CatalogAction';

const columns: ControlledTableColumns<ServiceCategory> = [
  {
    content: 'Name',
    key: 'name',
    required: true,
    sortable: true,
    width: 350,
    render: ({ id, name }: ServiceCategory): ReactElement => <Link to={`${id}/products`}>{name}</Link>,
  },
  {
    content: 'Category',
    key: 'category.id',
    render: ({ category }: ServiceCategory): ReactNode => (category ? category.name : null),
  },
];

const initialValues = {
  name: '',
  type: null,
  category: null,
};

const model = {
  endpoint: 'supplier/service_categories/',
  title: 'Service category',
  pluralTitle: 'Service categories',
  entityTitle: ({ name }: ServiceCategory) => name,
  permissions,
};

function ServiceCategortActions(): ReactElement {
  const ctx = useEntity();
  const navigate = useNavigate();

  const actions: EntityAction<any>[] = [
    {
      action: 'catalog',
      permKey: 'change',
      button: {
        icon: 'list-tree',
        children: 'Catalog',
        variant: 'secondary',
        size: 'sm',
        onClick: () =>
          openCatalogAction(ctx, (s) => {
            // dismissed
            if (!s) {
              return;
            }

            if (s.action === 'REMOVE' && !s.cascade) {
              navigate(`../`);
            }

            throw new Error(`Action not handled: ${s.action}`);
          }),
      },
    },
  ];

  return <EntityFormActions ml="5" actions={actions} />;
}

export const route = {
  path: 'service_categories',
  element: <ModelScene {...model} />,
  children: [
    {
      index: true,
      element: (
        <IndexScene
          columns={columns}
          filterFields={
            <>
              <SearchFilterInput />
              <AllActivityTypesFilterChip />
            </>
          }
          toolbar={
            <ImportExportActions
              import={{
                permission: permissions.import,
                endpoint: `${model.endpoint}import_xls/`,
                dialogChildren: (
                  <Alert variant="warning" className="py-0 px-1">
                    <Box fontSize="xs">Upserts products, services by names, kind and type.</Box>
                  </Alert>
                ),
              }}
              export={{
                permission: permissions.export,
                endpoint: `${model.endpoint}export_xls/`,
              }}
            />
          }
        />
      ),
    },
    {
      path: 'create',
      element: (
        <CreateScene initialValues={initialValues}>
          <OutletOrServiceCategoryForm />
        </CreateScene>
      ),
    },
    {
      path: ':id/',
      element: (
        <EntityScene>
          <Nav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: (
            <UpdateScene viewDisabled actions={<ServiceCategortActions />} deletePath={null}>
              <OutletOrServiceCategoryForm />
            </UpdateScene>
          ),
        },
        {
          path: 'products/*',
          element: <ServiceCategoryProducts />,
        },
      ],
    },
  ],
};
